import { Dictionary } from '../@types';

/**
 * `IReportingParamNameMap` allows product mutation events to be remapped to new objects specific to a tracking server
 * @property type: string -- the mutation type to map
 * @property paramMap: Dictionary<string> -- a key/value object of mutation properties (key) to map to tracking properties (value)
 * @property staticParams: Dictionary<string | number | boolean> -- an optional object of static properties to be added for the tracking server
 */
export interface IReportingParamNameMap {
	type: string;
	paramMap: Dictionary<string>;
	staticParams?: Dictionary<string | number | boolean>;
}

/**
 * `HttpMethod` is an enum used for POST or GET Http Calls
 */
export enum HttpMethod {
	POST = 'POST',
	GET = 'GET',
}

/**
 * `ITrackingServerConfig` defines information needed to connect to a tracking server
 * @property url: string -- the URL to send reports to
 * @property type: 'ajax' | 'script' -- the type of reporting server. 'ajax' hits an endpoint; 'script' calls a method
 * @property method: HttpMethod | string -- when type == 'script', the method to call to send reports; when 'ajax' designated POST or GET. @see HttpMethod
 * @property baseParams: Dictionary<string|number|boolean> -- an optional set of base params to send with every report
 * @property paramNameMap: IReportingParamNameMap[] -- an optional array mapping of known names to server-specific versions @see IReportingParamNameMap
 * @property thirdPartyUrls: Dictionary<string[]> -- an optional object of arrays of urls to be hit based on @see IReportingEvent type
 */
export interface ITrackingServerConfig {
	id: number;
	name: string;
	url: string;
	type: TrackingServerType | string;
	method: HttpMethod | string;
	baseParams?: Dictionary<
		string | number | boolean | Array<string | number | boolean> | object
	>;
	paramNameMapTemplate?: string;
	paramNameMap?: IReportingParamNameMap[];
	thirdPartyUrls?: Dictionary<string[]>;
	defaultTracking?: boolean;
}

/**
 * `TrackingServerType` denotes whether it is an ajax or script implementation
 */
export enum TrackingServerType {
	AJAX = 'AJAX',
	SCRIPT = 'Script',
}

export enum BaseParamNameTemplateType {
	SELECTABLE = 'Selectable',
	POLARIS = 'Polaris',
}
/**
 * `BaseParamNameTemplates` defines hard-coded maps from bellatrix events to
 * known tracking servers.
 * TODO: move this to an external JSON file, access by bellatrix-server
 */
export const BaseParamNameTemplates: Dictionary<IReportingParamNameMap[]> = {
	[BaseParamNameTemplateType.SELECTABLE]: [
		{
			type: 'checkout',
			paramMap: {
				type: 'status',
				retailer: 'c3',
			},
			staticParams: {
				type: 'buttonClicked',
			},
		},
		{
			type: 'showCart',
			paramMap: {
				type: 'status',
				retailer: 'c3',
			},
			staticParams: {
				type: 'buttonClicked',
			},
		},
		{
			type: 'hideCart',
			paramMap: {
				type: 'status',
				retailer: 'c3',
			},
			staticParams: {
				type: 'buttonClicked',
			},
		},
		{
			type: 'cartItemCarousel',
			paramMap: {
				type: 'status',
				retailer: 'c3',
			},
			staticParams: {
				type: 'buttonClicked',
			},
		},
		{
			type: 'cartItemList',
			paramMap: {
				type: 'status',
				retailer: 'c3',
			},
			staticParams: {
				type: 'buttonClicked',
			},
		},
		{
			type: 'nextCartItem',
			paramMap: {
				type: 'status',
				retailer: 'c3',
			},
			staticParams: {
				type: 'buttonClicked',
			},
		},
		{
			type: 'prevCartItem',
			paramMap: {
				type: 'status',
				retailer: 'c3',
			},
			staticParams: {
				type: 'buttonClicked',
			},
		},
		{
			type: 'hideCart',
			paramMap: {
				type: 'status',
				retailer: 'c3',
			},
			staticParams: {
				type: 'buttonClicked',
			},
		},
	],
	[BaseParamNameTemplateType.POLARIS]: [
		{
			type: 'foo',
			paramMap: {
				type: 'bar',
				abc: 'xyz',
			},
		},
	],
};
